import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "sonner";
import "@/App.css";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import DashboardShell from "@/components/DashboardShell";

import HomePage from "@/pages/HomePage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import JobsPage from "@/pages/JobsPage";
import JobDetailPage from "@/pages/JobDetailPage";
import AboutPage from "@/pages/AboutPage";
import AuthCallback from "@/pages/AuthCallback";

import AgencyDashboard from "@/pages/agency/AgencyDashboard";
import AgencyCandidates from "@/pages/agency/AgencyCandidates";
import AgencyEmployers from "@/pages/agency/AgencyEmployers";
import AgencyJobs from "@/pages/agency/AgencyJobs";
import AgencyPipeline from "@/pages/agency/AgencyPipeline";

import SuccessHome from "@/pages/agency/success/SuccessHome";
import LaunchChecklist from "@/pages/agency/success/LaunchChecklist";
import Playbooks from "@/pages/agency/success/Playbooks";
import Templates from "@/pages/agency/success/Templates";
import AIToolkit from "@/pages/agency/success/AIToolkit";
import GrowthDashboard from "@/pages/agency/success/GrowthDashboard";
import LearningCenter from "@/pages/agency/success/LearningCenter";
import AutomationCenter from "@/pages/agency/success/AutomationCenter";

import EmployerDashboard from "@/pages/employer/EmployerDashboard";
import EmployerJobs from "@/pages/employer/EmployerJobs";
import EmployerCandidates from "@/pages/employer/EmployerCandidates";

import CandidateDashboard from "@/pages/candidate/CandidateDashboard";
import CandidateProfile from "@/pages/candidate/CandidateProfile";
import CandidateApplications from "@/pages/candidate/CandidateApplications";
import CandidateSaved from "@/pages/candidate/CandidateSaved";

import MessagesPage from "@/pages/MessagesPage";
import InterviewsPage from "@/pages/InterviewsPage";

function ProtectedRoute({ children, roles }) {
  const { user } = useAuth();
  if (user === null) return <div className="p-10 text-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function PublicShell({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <div className="flex-1">{children}</div>
      <Footer />
    </div>
  );
}

function AppRouter() {
  // CRITICAL: If URL hash contains session_id (Google OAuth callback), render AuthCallback
  // over the entire route tree — DO NOT let Routes match first.
  if (typeof window !== "undefined" && window.location.hash && window.location.hash.includes("session_id=")) {
    return (
      <BrowserRouter>
        <AuthCallback />
      </BrowserRouter>
    );
  }
  return (
    <BrowserRouter>
      <Routes>
          <Route path="/" element={<PublicShell><HomePage /></PublicShell>} />
          <Route path="/about" element={<PublicShell><AboutPage /></PublicShell>} />
          <Route path="/jobs" element={<PublicShell><JobsPage /></PublicShell>} />
          <Route path="/jobs/:id" element={<PublicShell><JobDetailPage /></PublicShell>} />
          <Route path="/login" element={<PublicShell><LoginPage /></PublicShell>} />
          <Route path="/register" element={<PublicShell><RegisterPage /></PublicShell>} />

          {/* Agency (admin) */}
          <Route path="/dashboard/agency" element={
            <ProtectedRoute roles={["admin"]}>
              <DashboardShell portal="agency"><AgencyDashboard /></DashboardShell>
            </ProtectedRoute>
          } />
          <Route path="/dashboard/agency/candidates" element={
            <ProtectedRoute roles={["admin"]}>
              <DashboardShell portal="agency"><AgencyCandidates /></DashboardShell>
            </ProtectedRoute>
          } />
          <Route path="/dashboard/agency/employers" element={
            <ProtectedRoute roles={["admin"]}>
              <DashboardShell portal="agency"><AgencyEmployers /></DashboardShell>
            </ProtectedRoute>
          } />
          <Route path="/dashboard/agency/jobs" element={
            <ProtectedRoute roles={["admin"]}>
              <DashboardShell portal="agency"><AgencyJobs /></DashboardShell>
            </ProtectedRoute>
          } />
          <Route path="/dashboard/agency/pipeline" element={
            <ProtectedRoute roles={["admin"]}>
              <DashboardShell portal="agency"><AgencyPipeline /></DashboardShell>
            </ProtectedRoute>
          } />
          <Route path="/dashboard/agency/messages" element={
            <ProtectedRoute roles={["admin"]}>
              <DashboardShell portal="agency"><MessagesPage /></DashboardShell>
            </ProtectedRoute>
          } />
          <Route path="/dashboard/agency/interviews" element={
            <ProtectedRoute roles={["admin"]}>
              <DashboardShell portal="agency"><InterviewsPage /></DashboardShell>
            </ProtectedRoute>
          } />

          <Route path="/dashboard/agency/success" element={
            <ProtectedRoute roles={["admin"]}><DashboardShell portal="agency"><SuccessHome /></DashboardShell></ProtectedRoute>
          } />
          <Route path="/dashboard/agency/success/checklist" element={
            <ProtectedRoute roles={["admin"]}><DashboardShell portal="agency"><LaunchChecklist /></DashboardShell></ProtectedRoute>
          } />
          <Route path="/dashboard/agency/success/playbooks" element={
            <ProtectedRoute roles={["admin"]}><DashboardShell portal="agency"><Playbooks /></DashboardShell></ProtectedRoute>
          } />
          <Route path="/dashboard/agency/success/templates" element={
            <ProtectedRoute roles={["admin"]}><DashboardShell portal="agency"><Templates /></DashboardShell></ProtectedRoute>
          } />
          <Route path="/dashboard/agency/success/ai" element={
            <ProtectedRoute roles={["admin"]}><DashboardShell portal="agency"><AIToolkit /></DashboardShell></ProtectedRoute>
          } />
          <Route path="/dashboard/agency/success/growth" element={
            <ProtectedRoute roles={["admin"]}><DashboardShell portal="agency"><GrowthDashboard /></DashboardShell></ProtectedRoute>
          } />
          <Route path="/dashboard/agency/success/learning" element={
            <ProtectedRoute roles={["admin"]}><DashboardShell portal="agency"><LearningCenter /></DashboardShell></ProtectedRoute>
          } />
          <Route path="/dashboard/agency/success/automations" element={
            <ProtectedRoute roles={["admin"]}><DashboardShell portal="agency"><AutomationCenter /></DashboardShell></ProtectedRoute>
          } />

          {/* Employer */}
          <Route path="/dashboard/employer" element={
            <ProtectedRoute roles={["employer"]}>
              <DashboardShell portal="employer"><EmployerDashboard /></DashboardShell>
            </ProtectedRoute>
          } />
          <Route path="/dashboard/employer/jobs" element={
            <ProtectedRoute roles={["employer"]}>
              <DashboardShell portal="employer"><EmployerJobs /></DashboardShell>
            </ProtectedRoute>
          } />
          <Route path="/dashboard/employer/candidates" element={
            <ProtectedRoute roles={["employer"]}>
              <DashboardShell portal="employer"><EmployerCandidates /></DashboardShell>
            </ProtectedRoute>
          } />
          <Route path="/dashboard/employer/messages" element={
            <ProtectedRoute roles={["employer"]}>
              <DashboardShell portal="employer"><MessagesPage /></DashboardShell>
            </ProtectedRoute>
          } />
          <Route path="/dashboard/employer/interviews" element={
            <ProtectedRoute roles={["employer"]}>
              <DashboardShell portal="employer"><InterviewsPage /></DashboardShell>
            </ProtectedRoute>
          } />

          {/* Candidate */}
          <Route path="/dashboard/candidate" element={
            <ProtectedRoute roles={["candidate"]}>
              <DashboardShell portal="candidate"><CandidateDashboard /></DashboardShell>
            </ProtectedRoute>
          } />
          <Route path="/dashboard/candidate/profile" element={
            <ProtectedRoute roles={["candidate"]}>
              <DashboardShell portal="candidate"><CandidateProfile /></DashboardShell>
            </ProtectedRoute>
          } />
          <Route path="/dashboard/candidate/applications" element={
            <ProtectedRoute roles={["candidate"]}>
              <DashboardShell portal="candidate"><CandidateApplications /></DashboardShell>
            </ProtectedRoute>
          } />
          <Route path="/dashboard/candidate/saved" element={
            <ProtectedRoute roles={["candidate"]}>
              <DashboardShell portal="candidate"><CandidateSaved /></DashboardShell>
            </ProtectedRoute>
          } />
          <Route path="/dashboard/candidate/messages" element={
            <ProtectedRoute roles={["candidate"]}>
              <DashboardShell portal="candidate"><MessagesPage /></DashboardShell>
            </ProtectedRoute>
          } />
          <Route path="/dashboard/candidate/interviews" element={
            <ProtectedRoute roles={["candidate"]}>
              <DashboardShell portal="candidate"><InterviewsPage /></DashboardShell>
            </ProtectedRoute>
          } />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
  );
}

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors closeButton />
      <AppRouter />
    </AuthProvider>
  );
}

export default App;
